<template>
  <div>
    <div
      v-if="reviewData"
      class="row"
    >
      <div class="col-lg-8 mx-auto">
        <div class="card">
          <div class="card-header">
            <h5
              v-if="reviewData.order && reviewData.order.id"
              class="mb-0"
            >
              {{ $t('orders.order') + ' №' + reviewData.order.id }}
            </h5>
            <span v-if="reviewData.order.date_created">{{ $t('orders.made') }}: {{ reviewData.order.date_created | dateFormatter('YYYY-MM-DD HH:mm') }}</span> ->
            <span v-if="dateDelivered">{{ $t('orders.delivered') }}: {{ dateDelivered | dateFormatter('YYYY-MM-DD HH:mm') }}</span><br>
            <span class="mb-0">{{ $t('product.self_multiple') + ': ' }}<ShrinkStringDisplay
              :length="60"
              :string="reviewData.item.map(i => $l(i.title)).reduce((acc, curr) => acc + ' / ' + curr)"
            /> </span>
          </div>
          <div class="card-body">
            <div class="d-flex align-items-start flex-wrap border-bottom mb-3 pb-3">
              <div
                v-if="reviewData.author.photo"
                class="kit__utils__avatar kit__utils__avatar--size110 mr-3 mb-3 align-items-center flex-shrink-0"
              >
                <img
                  :src="mapImageObject(reviewData.author.photo).url"
                  :alt="reviewData.author.name"
                >
              </div>
              <a-avatar
                v-else
                shape="square"
                icon="user"
                size="large"
                :class="$style.avatar"
              />
              <div class="mb-3">
                <p><strong>{{ $t('common.date') }}:</strong> {{ reviewData.date_created | dateFormatter('YYYY-MM-DD HH:mm:ss') }}</p>
                <p><strong>{{ $t('review.author') }}:</strong> {{ reviewData.author.name }}</p>
                <p>
                  <strong>{{ $t('review.seller_rating') }}:</strong> <a-rate
                    :default-value="parseInt(reviewData.seller_rating)/10"
                    allow-half
                    disabled
                  />
                </p>
                <p class="font-italic">
                  {{ reviewData.seller_content }}
                </p>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <a-list
              item-layout="horizontal"
              :data-source="reviewData.item"
            >
              <a-list-item
                slot="renderItem"
                slot-scope="item"
              >
                <a-list-item-meta :description="item.content">
                  <MainImageView
                    slot="avatar"
                    :images="item && item.product_images ? item.product_images : []"
                    type="icon"
                  />
                  <a slot="title">
                    <p class="font-italic">{{ $l(item.title) }}</p>
                    <p><strong>{{ $t('review.rating') }}:</strong> <a-rate
                      :default-value="parseInt(item.rating)/10"
                      allow-half
                      disabled
                    /></p>
                    <div>
                      <span
                        v-for="idk in item.images"
                        :key="idk"
                      >
                        <ImageView
                          :image="idk"
                          type="icon"
                          height="40px"
                          width="40px"
                        />
                      </span>
                    </div>
                  </a>
                </a-list-item-meta>
              </a-list-item>
            </a-list>
            <!--            <a href="#" class="btn btn-sm btn-danger">Пожаловаться</a>-->
          </div>
        </div>
        <div class="card">
          <div
            v-if="reply"
            class="card-body"
          >
            <h5 class="text-dark mb-4">
              Ответ
            </h5>
            <p
              v-if="!editing"
              contenteditable="true"
              class="font-italic"
              @focus="handleFocus"
            >
              {{ reply.content }}
            </p>
            <a-form
              v-else
              @submit.prevent="handleEdit"
            >
              <a-form-item>
                <a-textarea
                  v-model="replyEdit"
                  :disabled="loading"
                  :rows="3"
                  :placeholder="$t('review.response')"
                />
              </a-form-item>
              <a-form-item>
                <a-button
                  class="mr-2"
                  type="primary"
                  html-type="submit"
                  style="width: 200px;"
                >
                  <i class="fa fa-save mr-2" /> {{ $t('common.save') }}
                </a-button>
                <a-button
                  :disabled="loading"
                  class="mr-2"
                  style="width: 200px;"
                  @click="handleBlur"
                >
                  {{ $t('common.cancel') }}
                </a-button>
              </a-form-item>
            </a-form>
          </div>
          <div
            v-else
            class="card-body"
          >
            <h5 class="text-dark mb-4">
              Ответить:
            </h5>
            <a-form
              :form="form"
              @submit.prevent="handleSubmit"
            >
              <a-form-item>
                <a-textarea
                  v-decorator="['response', { rules: [{ required: true, message: $t('review.response_validation') }] },]"
                  :disabled="loading"
                  :rows="3"
                  :placeholder="$t('review.response')"
                />
              </a-form-item>
              <a-form-item>
                <a-button
                  :disabled="loading"
                  class="mr-2"
                  type="primary"
                  html-type="submit"
                  style="width: 200px;"
                >
                  <i class="fa fa-send mr-2" /> {{ $t('common.send') }}
                </a-button>
              </a-form-item>
            </a-form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import reviewService from '@/services/api/manager/review-service'
import orderService from '@/services/api/manager/order-service'
import { mapActions, mapGetters } from 'vuex'
import AFormItem from 'ant-design-vue/es/form/FormItem'
import ShrinkStringDisplay from '@/components/common/ShrinkStringDisplay'
import MainImageView from '@/components/common/MainImageView'
import ImageView from '@/components/common/ImageView'

export default {
  name: 'ReviewDetails',
  components: { ShrinkStringDisplay, AFormItem, MainImageView, ImageView },
  data() {
    return {
      reviewId: this.$route.params.id,
      reviewData: null,
      loading: false,
      form: this.$form.createForm(this, { name: 'reviewResponse' }),
      reply: null,
      replyEdit: null,
      editing: false,
      dateDelivered: null,
    }
  },
  computed: {
    status () {
      return this.orderData?.status?.code ?? null
    },
    ...mapGetters({
      user: 'user/user',
    }),
  },
  async mounted () {
    this.read()
    await this.fetchReviewInfo()

    if (this.reviewData?.order?.hash_id) {
      const { data } = await orderService.fetchStatusHistory(this.reviewData?.order?.hash_id).catch(() => [])
      console.log(data)
      this.dateDelivered = data?.content?.find((entry) => entry.status?.code === 'DELIVERED')?.date_created ?? null
    }
  },
  methods: {
    ...mapActions({
      fetchNotifications: 'user/fetchNotifications',
    }),
    async fetchReviewInfo () {
      const { data } = await reviewService.getReview(this.reviewId).catch(() => null)
      this.reviewData = data?.content
      this.reply = data?.content?.response
      this.replyEdit = data?.content?.response?.content
    },
    read() {
      reviewService.readReview(this.reviewId)
        .then(() => {
          this.fetchNotifications()
        })
        .catch(() => null)
    },
    handleEdit() {
      reviewService.editReply(this.reviewId, this.reply.id, { content: this.replyEdit })
        .then((response) => {
          if (response && response.data)
            this.reply = response.data.content
        })
        .finally(() => {
          this.loading = false
          this.handleBlur()
        })
    },
    handleSubmit() {
      this.form.validateFields((err, values) => {
        if (!err) {
          reviewService.replyToReview(this.reviewId, { content: values.response })
            .then((response) => {
              if (response && response.data && response.data.content) {
                this.reply = response.data.content
                this.loading = false
                this.handleBlur()
              }
            })
        }
      })
    },
    handleFocus() {
      this.editing = true
    },
    handleBlur() {
      this.editing = false
      this.replyEdit = this.reply?.content
    },
    mapImageObject (image) {
      if (image.full) {
        return {
          url: image.full.path.startsWith('http') ? image.full.path : baseMediaPath + image.full.path,
          status: 'done',
          name: image.id + image.full.path.substr(image.full.path.lastIndexOf('.')),
          uid: image.id,
        }
      }
    },
  },
}

const baseMediaPath = process.env.VUE_APP_API_CDN
</script>

<style lang="scss" module>
  @import "../../../components/cleanui/layout/Topbar/UserMenu/style.module.scss";
</style>
